import React from "react";
import { Link } from "react-router-dom";
import { useLocale } from "../context/LocaleContext";
import { ArrowRight } from "lucide-react";
import { useShop } from "../context/ShopContext";
import { PageTitle } from "../components/Shared";
import Reviews from "../components/Reviews";
import Seo from "../components/Seo";

const content = {
  it: {
    title: "Agenzia web & comunicazione",
    seoDesc: "IdeaMedia, agenzia web e comunicazione di Padova: dal 1999 soluzioni web, software e marketing per la piccola e media impresa. Oltre 250 clienti in Italia e all'estero.",
    storyTitle: "Storia e filosofia aziendale",
    story1: "IdeaMedia agenzia Web e Comunicazione nasce nel lontano 2015, a seguito della chiusura e del rebranding di Creazioni Multimedia, a sua volta nata nel 1999 agli albori italiani del web e di internet, quasi per gioco. L'azienda si \u00e8 sviluppata nel corso degli anni come partner ideale per la PMI (piccola e media impresa) fornendo valide ed ottime soluzioni per incrementare il business di tali realt\u00e0. Attualmente conta pi\u00f9 di 250 clienti in tutta Italia ed estero, offrendo soluzioni informatiche e servizi web semplici ed efficaci.",
    story2: "La filosofia di base dell'azienda \u00e8 quella di ampliare e migliorare la comunicazione aziendale, fattore fondamentale sia all'interno dell'impresa (utile per aumentare la produttivit\u00e0 interna) che anche all'esterno dell'impresa, dove se applicata correttamente rende possibile aumentare la branding awareness (ossia la visibilit\u00e0 aziendale) con la relativa richiesta del proprio prodotto o servizio, posizionamento e conversione.",
    story3: "Quindi tutti i prodotti e servizi di IdeaMedia si basano su questi concetti ed offrono ai suoi clienti un veritiero incremento di vendite e fatturato concretizzando in breve tempo l'investimento effettuato. Qui in basso abbiamo una breve descrizione dei vari servizi che offriamo.",
    servicesTitle: "Breve descrizione dei servizi",
    techTitle: "Servizi tecnici",
    techItems: [
      "Sviluppo siti web Vetrina",
      "Sviluppo siti web E-commerce",
      "Sviluppo siti web custom",
      "Sviluppo piattaforme web",
      "Sviluppo gestionali web & in cloud",
      "Sviluppo integrazioni gestionali / siti web",
      "Sviluppo web application",
      "Sviluppo applicazioni native",
    ],
    mktTitle: "Servizi di marketing",
    mktItems: [
      "Ottimizzazioni SEO",
      "Analisi del Brand",
      "Campagne marketing SEM (Google Search engine)",
      "Campagne marketing Social Facebook / Instagram / Linkedin",
      "Campagne marketing diretto email (DEM) e SMS",
      "Raccolta dati e prodotti da competitor",
      "Gestione ed invio Newsletter in grandi quantit\u00e0",
      "Utilizzo Database B2B aziendali italiani e stranieri certificati",
    ],
    blocks: [
      { img: "/images/agenzia-staff.jpg", title: "Chi siamo - il nostro staff", cta: "Vedi dettagli", to: "/team" },
      { img: "/images/agenzia-realizzazioni.jpg", title: "Nostre realizzazioni - cosa facciamo", cta: "Vai al portfolio", to: "/portfolio" },
      { img: "/images/agenzia-news.png", title: "Consigli e novit\u00e0 sul mondo informatico", cta: "Scopri le novit\u00e0", to: "/shop" },
    ],
  },
  en: {
    title: "Web & communication agency",
    seoDesc: "IdeaMedia, web and communication agency in Padua, Italy: since 1999 web, software and marketing solutions for small and medium businesses. Over 250 clients in Italy and abroad.",
    storyTitle: "Company history and philosophy",
    story1: "IdeaMedia Web and Communication Agency was born back in 2015, following the closure and rebranding of Creazioni Multimedia, which in turn was founded in 1999 at the dawn of the Italian web, almost as a game. Over the years the company has grown into the ideal partner for SMBs (small and medium businesses), providing valuable and effective solutions to grow their business. Today it counts more than 250 clients throughout Italy and abroad, offering simple and effective IT solutions and web services.",
    story2: "The company's core philosophy is to expand and improve business communication, a fundamental factor both inside the company (useful to increase internal productivity) and outside it, where, if applied correctly, it makes it possible to increase brand awareness (i.e. company visibility) with the related demand for one's product or service, positioning and conversion.",
    story3: "All IdeaMedia products and services are therefore based on these concepts and offer clients a real increase in sales and revenue, quickly turning the investment into concrete results. Below is a brief description of the services we offer.",
    servicesTitle: "A brief overview of our services",
    techTitle: "Technical services",
    techItems: [
      "Showcase website development",
      "E-commerce website development",
      "Custom website development",
      "Web platform development",
      "Web & cloud management software development",
      "ERP / website integrations development",
      "Web application development",
      "Native app development",
    ],
    mktTitle: "Marketing services",
    mktItems: [
      "SEO optimization",
      "Brand analysis",
      "SEM marketing campaigns (Google Search engine)",
      "Social marketing campaigns Facebook / Instagram / Linkedin",
      "Direct email (DEM) and SMS marketing campaigns",
      "Competitor data and product collection",
      "Large-scale newsletter management and sending",
      "Certified Italian and foreign B2B company databases",
    ],
    blocks: [
      { img: "/images/agenzia-staff.jpg", title: "Who we are - our staff", cta: "See details", to: "/team" },
      { img: "/images/agenzia-realizzazioni.jpg", title: "Our work - what we do", cta: "Go to portfolio", to: "/portfolio" },
      { img: "/images/agenzia-news.png", title: "Tips & news from the IT world", cta: "Discover what's new", to: "/shop" },
    ],
  },
};

const Agenzia = () => {
  const { lang } = useShop();
  const { lp } = useLocale();
  const c = content[lang];

  return (
    <div data-testid="agency-page">
      <Seo title={c.title} description={c.seoDesc} />
      <PageTitle title={c.title} />

      {/* Banner with watercolor skyline (original) + section title */}
      <section
        className="idm-agz-banner"
        style={{ backgroundImage: "url(/images/agenzia-storia-bg.jpg)" }}
        data-testid="agency-banner"
      >
        <h2>{c.storyTitle}</h2>
      </section>

      {/* Story paragraphs (original text) */}
      <section className="idm-section" style={{ paddingTop: 56 }}>
        <div className="idm-container idm-agz-story" data-testid="agency-story">
          <p className="idm-text">{c.story1}</p>
          <p className="idm-text">{c.story2}</p>
          <p className="idm-text">{c.story3}</p>
        </div>
      </section>

      {/* Services overview - two columns like the original */}
      <section className="idm-section idm-section-gray" data-testid="agency-services">
        <div className="idm-container">
          <h2 className="idm-center idm-agz-h2">{c.servicesTitle}</h2>
          <span className="idm-separator" />
          <div className="idm-svc-cols">
            <div className="idm-svc-col">
              <h3>{c.techTitle}</h3>
              <ul className="idm-svc-list">
                {c.techItems.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
            <div className="idm-svc-col">
              <h3>{c.mktTitle}</h3>
              <ul className="idm-svc-list">
                {c.mktItems.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* "Chi siamo" blocks - cloned from the original page */}
      <section className="idm-section" data-testid="agency-blocks">
        <div className="idm-container">
          <div className="idm-agz-blocks">
            {c.blocks.map((b) => (
              <div className="idm-agz-block" key={b.to} data-testid={`agency-block-${b.to.replace("/", "")}`}>
                <Link to={lp(b.to)} className="idm-agz-block-img">
                  <img src={b.img} alt={b.title} loading="lazy" />
                </Link>
                <h3>{b.title}</h3>
                <Link to={lp(b.to)} className="idm-agz-block-link">
                  {b.cta} <ArrowRight size={14} />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Reviews - automatically fetched from public Google profile */}
      <Reviews />
    </div>
  );
};

export default Agenzia;
